import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import type { BlockedWorkspaceSwitch } from '../lib/workspace-switch-identity';

type WorkspaceSwitchBlockedDialogProps = {
  blockedSwitch: BlockedWorkspaceSwitch | null;
  onClose: () => void;
};

const closeButtonSx = (theme: Theme) => ({
  flex: 1,
  borderRadius: 1.5,
  fontWeight: 700,
  textTransform: 'none' as const,
  py: 1.25,
  color: theme.palette.grey[50],
  borderColor: theme.palette.grey[700],
  '&:hover': {
    bgcolor: theme.palette.grey[800],
    borderColor: theme.palette.grey[600],
  },
});

/** Explains why a workspace switch signed the user out. See CLOUD-715. */
export function WorkspaceSwitchBlockedDialog({ blockedSwitch, onClose }: WorkspaceSwitchBlockedDialogProps) {
  const { t: translate } = useTranslation();

  const message = blockedSwitch?.actualEmail
    ? translate('workspaces.switchBlockedMessage', {
        expected: blockedSwitch.expectedEmail,
        actual: blockedSwitch.actualEmail,
      })
    : translate('workspaces.switchBlockedMessageUnknown', { expected: blockedSwitch?.expectedEmail ?? '' });

  return (
    <Dialog
      open={Boolean(blockedSwitch)}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      data-testid="cloud.workspace.switch-blocked-dialog"
      slotProps={{
        paper: { sx: { bgcolor: 'container.low', borderRadius: 2, textAlign: 'center' } },
        backdrop: { sx: { bgcolor: 'rgba(0, 0, 0, 0.85)' } },
      }}
    >
      <DialogTitle sx={{ textAlign: 'center' }}>{translate('workspaces.switchBlockedTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body2" sx={(theme) => ({ color: theme.palette.grey[400] })}>
            {message}
          </Typography>
          <Typography variant="body2" sx={(theme) => ({ color: theme.palette.grey[400] })}>
            {translate('workspaces.switchBlockedHint')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          data-testid="cloud.workspace.switch-blocked-dialog.close"
          variant="outlined"
          onClick={onClose}
          sx={closeButtonSx}
        >
          {translate('accessibility.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
